import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Logo from "@/components/Logo";
import { ArrowLeft, Loader2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Page() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/pages/${slug}`)
      .then((res) => {
        if (!cancelled) setPage(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="page-back-btn"
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition"
          >
            <ArrowLeft className="w-3 h-3 text-[#39FF14]" /> Back to Home
          </button>
        </div>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="page-logo"><Logo /></button>
          <button
            onClick={() => navigate("/products")}
            className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#39FF14] transition"
          >
            Browse Catalog →
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(57,255,20,0.07),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-20">
          {loading ? (
            <div className="flex items-center gap-3 text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : notFound ? (
            <div data-testid="page-not-found" className="flex flex-col items-start gap-3">
              <FileText className="w-10 h-10 text-zinc-700" />
              <h1 className="font-headline text-3xl font-black">Page not found</h1>
              <p className="text-zinc-500 text-sm">The page <code className="text-zinc-300">/{slug}</code> does not exist.</p>
            </div>
          ) : (
            <>
              {page.eyebrow && (
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#39FF14] font-bold mb-3">
                  {page.eyebrow}
                </p>
              )}
              <h1 data-testid="page-title" className="font-headline text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-3">
                {page.title}
              </h1>
              {page.updated_at && (
                <p className="text-[11px] uppercase tracking-wider text-zinc-600">
                  Last updated: {new Date(page.updated_at).toLocaleDateString()}
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {!loading && !notFound && page && (
        <section className="px-6 py-12 lg:py-16">
          <article
            data-testid="page-content"
            className="max-w-3xl mx-auto page-markdown"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.content || ""}</ReactMarkdown>
          </article>
        </section>
      )}
    </div>
  );
}
